import {
  parseEventLogs,
  type Abi,
  type Address,
  type Hash,
  type TransactionReceipt,
} from "viem";
import config from "../../config/config";
import { hasProfileForAccount, isProfileCreationWrite } from "../profileAccess";
import { appChain } from "./chain";
import {
  getPublicClient,
  getWalletAccount,
  getWalletClient,
  getWalletPublicClient,
} from "./clients";
import { withCommonErrors } from "./commonErrors";
import {
  decodeContractError,
  resolveContractWriteError,
  toSerializableContractError,
} from "./decodeContractError";
import { serialize, toBigInt } from "./serialize";

type AbiOutput = {
  name?: string;
  type?: string;
  components?: AbiOutput[];
};

const attachNamedOutputs = (outputs: AbiOutput[] | undefined, value: unknown): unknown => {
  if (!outputs?.length) return value;

  if (outputs.length === 1) {
    return attachSingleOutput(outputs[0], value);
  }

  const values = outputs.map((output, index) =>
    attachSingleOutput(
      output,
      Array.isArray(value) ? value[index] : (value as any)?.[output.name || index]
    )
  );

  outputs.forEach((output, index) => {
    if (output.name) {
      (values as any)[output.name] = values[index];
    }
  });

  return values;
};

const attachSingleOutput = (output: AbiOutput, value: unknown): unknown => {
  if (output.type === "tuple") {
    return attachNamedOutputs(output.components, value);
  }
  if (output.type === "tuple[]" && Array.isArray(value)) {
    return value.map((item) => attachNamedOutputs(output.components, item));
  }
  return value;
};

const withAbiNames = (abi: any, functionName: string, result: unknown) => {
  const items = Array.isArray(abi) ? abi : abi?.abi;
  const fn = items?.find(
    (item: any) => item?.type === "function" && item?.name === functionName
  );
  return attachNamedOutputs(fn?.outputs, result);
};

export type ContractCallParams = {
  address: string;
  abi: Abi | readonly unknown[] | any;
  functionName: string;
  args?: readonly unknown[];
  account?: string;
  /** Read via the wallet node so VRF/block-wait checks match the node that will send the next tx. */
  useWallet?: boolean;
};

export type ContractWriteParams = ContractCallParams & {
  value?: bigint | string | number;
  gas?: bigint | string | number;
  gasMultiplier?: number;
  /** Use this chain's max transaction gas (BNB vs PLS differ). */
  useMaxGas?: boolean;
};

export type TxReceipt = TransactionReceipt & {
  transactionHash: Hash;
};

const toOptionalBigInt = (value?: bigint | string | number) => {
  if (value === undefined) return undefined;
  if (typeof value === "bigint") return value;
  return BigInt(value);
};

export const readContract = async <T = any>({
  address,
  abi,
  functionName,
  args,
  account,
  useWallet,
}: ContractCallParams): Promise<T> => {
  const client = useWallet ? getWalletPublicClient() : getPublicClient();
  const result = await client.readContract({
    address: address as Address,
    abi: abi as Abi,
    functionName,
    args,
    ...(account ? { account: account as Address } : {}),
  } as any);

  return serialize(withAbiNames(abi, functionName, result)) as T;
};

export const waitForConfirmation = async (
  txHash: string,
  abi?: Abi | readonly unknown[]
): Promise<TxReceipt> => {
  const receipt = await getWalletPublicClient().waitForTransactionReceipt({
    hash: txHash as Hash,
    pollingInterval: 1000,
  });

  if (receipt.status === "reverted") {
    try {
      const client = getWalletPublicClient();
      const tx = await client.getTransaction({ hash: txHash as Hash });
      await client.call({
        to: tx.to ?? undefined,
        data: tx.input,
        account: tx.from,
        value: tx.value,
        blockNumber: receipt.blockNumber,
      });
    } catch (error) {
      throw toSerializableContractError(decodeContractError(error, abi), error);
    }
    throw new Error("Transaction reverted");
  }

  return receipt;
};

export const waitForBlocks = async (
  confirmedBlockNumber: number | bigint | string,
  blocksToWait: number = 2
): Promise<void> => {
  const client = getWalletPublicClient();

  let confirmed = 0n;
  try {
    confirmed = toBigInt(confirmedBlockNumber);
  } catch {
    confirmed = 0n;
  }

  const firstSeen = await client.getBlockNumber();
  // If the receipt block is missing or from a different node (far behind),
  // wait N blocks from what this wallet currently sees instead of returning immediately.
  const looksInvalid = confirmed <= 0n || firstSeen > confirmed + 256n;
  const targetBlockNumber = looksInvalid
    ? firstSeen + BigInt(blocksToWait)
    : confirmed + BigInt(blocksToWait);

  while (true) {
    const currentBlock = await client.getBlockNumber();
    if (currentBlock >= targetBlockNumber) {
      return;
    }
    await new Promise((resolve) => setTimeout(resolve, 3000));
  }
};

export const writeContractAndWait = async ({
  address,
  abi,
  functionName,
  args,
  account,
  value,
  gas,
  gasMultiplier,
  useMaxGas,
}: ContractWriteParams): Promise<TxReceipt> => {
  const walletAccount = getWalletAccount(account);
  if (!isProfileCreationWrite(address, functionName, args, config.profileAddress, config.mafiaAddress, config.profileUnboxAddress)) {
    // Load the store at write time to avoid a module cycle through profile thunks.
    const { store } = await import("@/app/store");
    if (!hasProfileForAccount(walletAccount, store.getState().profile)) {
      throw new Error("Create a profile before using game contracts.");
    }
  }

  const publicClient = getPublicClient();
  const walletClient = getWalletClient(account);
  const mergedAbi = withCommonErrors(abi);
  const writeArgs = {
    address: address as Address,
    abi: mergedAbi,
    functionName,
    args,
    account: walletAccount,
    chain: appChain,
    value: toOptionalBigInt(value),
    gas: useMaxGas && gas === undefined
      ? BigInt(config.maxTxGas)
      : toOptionalBigInt(gas),
  };

  const rethrowDecoded = (error: unknown) =>
    resolveContractWriteError({
      error,
      clients: [
        publicClient as { simulateContract: (args: any) => Promise<unknown> },
        getWalletPublicClient() as { simulateContract: (args: any) => Promise<unknown> },
      ],
      writeArgs: {
        address: writeArgs.address,
        abi: mergedAbi,
        functionName,
        args,
        account: walletAccount,
        value: writeArgs.value,
      },
    });

  if (gasMultiplier && writeArgs.gas === undefined) {
    try {
      const estimated = await publicClient.estimateContractGas(writeArgs as any);
      writeArgs.gas = estimated * BigInt(Math.ceil(gasMultiplier));
    } catch (error) {
      return await rethrowDecoded(error);
    }
  }

  let hash: Hash;
  try {
    hash = await walletClient.writeContract(writeArgs as any);
    console.log("tx", hash);
  } catch (error) {
    return await rethrowDecoded(error);
  }
  return waitForConfirmation(hash, mergedAbi);
};

const withEventNames = (abi: any, eventName: string, args: unknown) => {
  const items = Array.isArray(abi) ? abi : abi?.abi;
  const event = items?.find(
    (item: any) => item?.type === "event" && item?.name === eventName
  );
  return attachNamedOutputs(event?.inputs, args);
};

export const getDecodedEvent = (
  receipt: TransactionReceipt,
  abi: Abi | readonly unknown[] | any,
  eventName: string
) => {
  const logs = parseEventLogs({
    abi: abi as Abi,
    logs: receipt.logs,
    eventName,
  } as any);

  if (!logs.length) {
    return undefined;
  }

  return {
    returnValues: serialize(
      withEventNames(abi, eventName, (logs[0] as any).args)
    ),
  };
};
